import type { IconEnabledForComps } from '../types/categories';
import { HoverImageAction } from '../types/HoverImageAction';
import { InfoIconMode } from '../types/InfoIconMode';
import { LabelMode } from '../types/LabelMode';
import type { PresetSection, PresetSectionItem, SectionBase } from '../types/sections';
import { SectionType } from '../types/SectionType';
import { SectionBaseBuilder, SectionPropsWithoutSectionBase } from './SectionBaseBuilder';

export class PresetSectionBuilder extends SectionBaseBuilder {
  private section: SectionPropsWithoutSectionBase<PresetSection, 'props' | 'type'>;

  constructor() {
    super();

    this.section = {
      type: SectionType.Preset,
      additionalBehaviours: {
        labelMode: LabelMode.Always,
        infoIcon: InfoIconMode.Always,
        iconEnabledForComps: {},
        hoverImageAction: HoverImageAction.Image,
        scaleProportion: 0,
      },
      props: {
        compTypes: [],
        image: 'https://example.com/image.jpg',
        imageHover: '',
        retinaImage: '',
        items: [],
      },
      isImageSection: false,
      isDisabled: false,
      topTitle: '',
      rebuildTrigger: '',
      teaser: {
        id: '',
        hide: false,
        title: '',
        text: '',
        linkText: '',
        image: '',
        video: '',
        helpId: '',
        helpCompType: '',
      },
      liveTextSection: false,
    };
  }

  withAdditionalBehaviours(additionalBehaviours: PresetSection['additionalBehaviours']): this {
    this.section.additionalBehaviours = additionalBehaviours;
    return this;
  }

  withProps(props: PresetSection['props']): this {
    this.section.props = props;
    return this;
  }

  withIsImageSection(isImageSection: PresetSection['isImageSection']): this {
    this.section.isImageSection = isImageSection;
    return this;
  }

  withIsDisabled(isDisabled: PresetSection['isDisabled']): this {
    this.section.isDisabled = isDisabled;
    return this;
  }

  withAdditionalScaling(additionalScaling: PresetSection['additionalScaling']): this {
    this.section.additionalScaling = additionalScaling;
    return this;
  }

  withTopTitle(topTitle: PresetSection['topTitle']): this {
    this.section.topTitle = topTitle;
    return this;
  }

  withRebuildTrigger(rebuildTrigger: PresetSection['rebuildTrigger']): this {
    this.section.rebuildTrigger = rebuildTrigger;
    return this;
  }

  withTeaser(teaser: PresetSection['teaser']): this {
    this.section.teaser = teaser;
    return this;
  }

  withLiveTextSection(liveTextSection: PresetSection['liveTextSection']): this {
    this.section.liveTextSection = liveTextSection;
    return this;
  }

  withImage(image: string): this {
    this.section.props.image = image;
    return this;
  }

  withImageHover(imageHover: string): this {
    this.section.props.imageHover = imageHover;
    return this;
  }

  withHoverImageAction(action: HoverImageAction): this {
    this.section.additionalBehaviours.hoverImageAction = action;
    return this;
  }

  withLabelMode(mode: LabelMode): this {
    this.section.additionalBehaviours.labelMode = mode;
    return this;
  }

  withIconEnabledForComps(iconEnabledForComps: IconEnabledForComps): this {
    this.section.additionalBehaviours.iconEnabledForComps = iconEnabledForComps;
    return this;
  }

  withItems(...items: PresetSectionItem[]): this {
    this.section.props.items = items;
    return this;
  }

  buildBase(): SectionBase {
    return super.build();
  }

  buildWithoutItems(): Omit<PresetSection, 'props'> & { props: Omit<PresetSection['props'], 'items'> } {
    const { props, ...otherSectionFields } = this.build();
    const { items, ...otherProps } = props;

    return { ...otherSectionFields, props: otherProps };
  }

  build(): PresetSection {
    return { ...this.buildBase(), ...this.section };
  }
}
